/**
 * TrackingAuditHistory - Timeline view of hard-copy tracking events.
 */

import React, { useState, useEffect } from 'react';
import { Clock, User, FileText, Printer, Send, PenTool, Upload, XCircle } from 'lucide-react';
import type { TrackingAuditEvent } from '@/types/hardCopyTracking';
import hardCopyTrackingAPI from '@/services/api/hardCopyTracking';

interface TrackingAuditHistoryProps {
  trackingId: number;
}

const ACTION_CONFIG: Record<string, { icon: any; label: string; color: string }> = {
  hardcopy_generated: { icon: FileText, label: 'Tracking record created', color: 'text-gray-500' },
  hardcopy_printed: { icon: Printer, label: 'Document printed', color: 'text-blue-500' },
  hardcopy_provided: { icon: Send, label: 'Provided for signature', color: 'text-amber-500' },
  hardcopy_signed: { icon: PenTool, label: 'Member signature recorded', color: 'text-purple-500' },
  hardcopy_scan_uploaded: { icon: Upload, label: 'Signed copy uploaded', color: 'text-green-500' },
  hardcopy_cancelled: { icon: XCircle, label: 'Tracking cancelled', color: 'text-red-500' },
};

const formatDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

export function TrackingAuditHistory({ trackingId }: TrackingAuditHistoryProps) {
  const [events, setEvents] = useState<TrackingAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [trackingId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await hardCopyTrackingAPI.getHistory(trackingId);
      setEvents(data.events);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">No events recorded yet.</p>
    );
  }

  return (
    <div className="space-y-0">
      {events.map((event, idx) => {
        const config = ACTION_CONFIG[event.action] || {
          icon: Clock, label: event.action, color: 'text-gray-400',
        };
        const Icon = config.icon;
        const isLast = idx === events.length - 1;

        return (
          <div key={event.id} className="flex gap-3">
            {/* Timeline line + dot */}
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-white border-2 ${config.color.replace('text-', 'border-')}`}>
                <Icon className={`w-4 h-4 ${config.color}`} />
              </div>
              {!isLast && <div className="w-0.5 flex-1 bg-gray-200 my-1" />}
            </div>

            {/* Event content */}
            <div className={`pb-4 ${isLast ? '' : 'border-b-0'}`}>
              <p className="text-sm font-medium">{config.label}</p>
              <div className="flex items-center gap-2 mt-0.5">
                {event.performed_by_name && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {event.performed_by_name}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(event.created_at)}
                </span>
              </div>
              {/* Extra metadata */}
              {event.metadata?.member_signed_at && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Signed on: {event.metadata.member_signed_at}
                </p>
              )}
              {event.metadata?.reason && (
                <p className="text-xs text-red-600 mt-0.5">
                  Reason: {event.metadata.reason}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
